import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import Disclaimers from '../components/taxPlanner/Disclaimers';
import PositionsTable from '../components/taxPlanner/PositionsTable';
import PlannerParameters from '../components/taxPlanner/PlannerParameters';
import ReportsData from '../components/taxPlanner/ReportsData';
import SummaryBlock from '../components/taxPlanner/SummaryBlock';
import EntryWindow from '../components/taxPlanner/EntryWindow';
import PlannerToast from '../components/taxPlanner/PlannerToast';
import PlannerUnavailable from '../components/taxPlanner/PlannerUnavailable';
import LinkButton from '../components/LinkButton';
import Skeleton from '../components/Skeleton';
import UploadIcon from '../components/icons/UploadIcon';
import { MY_ACCOUNTS_PATH } from '../constants/syncRoutes';
import { chooseEntryWindow } from '../helpers/taxPlanner/entryWindow';
import useNavigation from '../hooks/use-navigation';
import { useLazyReadTaxPlannerSnapshotQuery } from '../store/apis/taxApi';
import { enterPlanner, leave, proceed } from '../store/slices/taxPlannerSlice';

const UPLOAD_PATH = '/unified-upload';
const CALCULATION_PATH = '/taxreports';
// SCR-05 (questionable-event-confirmation) has no route in osa-web yet: the account list is where unresolved operations are reached.
const CLARIFY_PATH = MY_ACCOUNTS_PATH;

const TARGETS = { upload: UPLOAD_PATH, clarify: CLARIFY_PATH };

// The container of the new screens (as InvestingAccountPage), mock `.content`: 120 px under the last block keep
// «Важливо» clear of the DirtyBar (B2-09).
const CONTAINER = 'px-4 md:px-10 pt-8 pb-[120px] max-w-[1040px]';

// Number-free placeholder behind the entry window (AC-30b): SCR-02's shape blurred by 7 px (mock .blurred, B-20).
function Backdrop() {
  return (
    <div data-testid="planner-backdrop" aria-hidden="true" className="pointer-events-none flex select-none flex-col blur-[7px]">
      <div data-testid="backdrop-header" className="flex flex-col gap-2">
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-4 w-full max-w-[600px]" />
      </div>
      <div data-testid="backdrop-tiles" className="mt-[22px] grid grid-cols-1 gap-3 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} data-testid="backdrop-tile" className="h-28 w-full" />
        ))}
      </div>
      <div className="mt-9 grid grid-cols-1 gap-3 mid:grid-cols-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
      <Skeleton data-testid="backdrop-table" className="mt-9 h-72 w-full" />
    </div>
  );
}

function TaxPlannerPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { navigate } = useNavigation();
  const auth = useAuth();
  const sub = auth.user?.profile?.sub;
  const [readSnapshot, { data: cached, error: failed, isFetching }] = useLazyReadTaxPlannerSnapshotQuery();
  // While a request of this visit is in flight, a result of the previous one is not shown (PO-1).
  const data = isFetching ? undefined : cached;
  const error = isFetching ? undefined : failed;
  const entered = useRef(false);
  const enteredSnapshot = useSelector((state) => state.taxPlanner.snapshot);
  const lastPick = useSelector((state) => state.taxPlanner.lastPick);
  const committedTotals = useSelector((state) => state.taxPlanner.committedTotals);
  const continued = useSelector((state) => state.taxPlanner.continued);
  const [staleAcknowledged, setStaleAcknowledged] = useState(false);
  const [now] = useState(() => new Date());

  // One snapshot per visit (ADR-0003): read on mount only; the bearer is taken per request, so a silent renew is neither
  // a request nor an entry.
  useEffect(() => {
    dispatch(leave());
    readSnapshot();
    return () => dispatch(leave());
  }, [dispatch, readSnapshot]);

  const ready = !error && data?.state === 'READY';
  useEffect(() => {
    if (!ready || entered.current) return;
    entered.current = true;
    dispatch(enterPlanner({ snapshot: data, sub }));
  }, [ready, data, sub, dispatch]);

  // READY waits until the slice holds this very snapshot, so the window never shows a pick of an older visit.
  const current = ready && enteredSnapshot === data;
  const waiting = ready && !current;
  const planner = continued && current;
  const entry = useMemo(
    () => chooseEntryWindow({ response: data, error, pick: ready ? lastPick : null, staleAcknowledged, now }),
    [data, error, ready, lastPick, staleAcknowledged, now],
  );

  if (entry.variant === 'unavailable') {
    return (
      <div className={CONTAINER} data-testid="tax-planner-page">
        <PlannerUnavailable onToCalculation={() => navigate(CALCULATION_PATH)} />
      </div>
    );
  }

  return (
    <div className={CONTAINER} data-testid="tax-planner-page">
      {planner ? (
        <div className="flex flex-col">
          {/* Mock order (B-01): header → totals → loss/target → positions → «Важливо»; gaps 22 / 36 px (.totals, .section). */}
          <header data-testid="planner-header" className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[.06em] text-text-muted">
                {t('taxPlanner.scr02.header.eyebrow', { year: enteredSnapshot?.taxYear ?? now.getFullYear() })}
              </p>
              <h1 className="mt-1.5 text-2xl font-semibold tracking-[-.02em] text-text">{t('taxPlanner.page.title')}</h1>
              <p className="mt-1.5 max-w-[600px] text-sm text-text-muted">{t('taxPlanner.scr02.header.subtitle')}</p>
              <ReportsData />
            </div>
            <LinkButton className="mt-1.5" onClick={() => navigate(UPLOAD_PATH)}>
              <UploadIcon />
              {t('taxPlanner.scr02.reports.update')}
            </LinkButton>
          </header>
          <div className="mt-[22px]">
            <SummaryBlock />
          </div>
          <div className="mt-9">
            <PlannerParameters />
          </div>
          <div className="mt-9">
            <PositionsTable />
          </div>
          <div className="mt-9">
            <Disclaimers />
          </div>
          <PlannerToast />
        </div>
      ) : (
        <Backdrop />
      )}
      {!planner && (
        <EntryWindow
          variant={waiting ? 'loading' : entry.variant}
          snapshot={data}
          staleAccounts={entry.staleAccounts}
          pick={lastPick}
          totals={committedTotals}
          now={now}
          onRetry={() => readSnapshot()}
          onProceed={() => dispatch(proceed())}
          onAcknowledgeStale={() => setStaleAcknowledged(true)}
          onNavigate={(target) => navigate(TARGETS[target])}
        />
      )}
    </div>
  );
}

export default TaxPlannerPage;
